import React from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '@/components/EmptyState';
import { ExternalLinkCard } from '@/components/ExternalLinks/ExternalLinkCard';
import { ArrowLeft } from '@/components/Icons/ArrowLeft';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { useOpenExternalLink } from '@/hooks/useOpenExternalLink';
import { Layout } from '@/layouts/Layout';
import type { LinkResponse } from '@/types/links';
import { goToPreviousPage } from '@/utils/global';

interface Props {
  title: string;
  links: LinkResponse[] | undefined;
  isLoading: boolean;
}

export const ExternalLinksView: React.FC<Props> = ({
  title,
  links,
  isLoading,
}) => {
  const { t } = useTranslation();
  const openLink = useOpenExternalLink();

  const mobileHeader = (
    <div className="flex w-full items-center md:hidden">
      <button
        type="button"
        onClick={goToPreviousPage}
        aria-label={t('global.back')}
        className="flex size-6 shrink-0 cursor-pointer items-center justify-center text-foreground"
      >
        <ArrowLeft />
      </button>
    </div>
  );

  const isEmpty = !isLoading && !links?.length;

  return (
    <Layout hideMobileNavbar mobileHeader={mobileHeader}>
      <div className="flex flex-1 flex-col gap-4 px-4 pb-8 md:px-6 md:pt-6">
        <h1 className="text-[24px]/[30px] font-semibold text-foreground md:text-title-24">
          {title}
        </h1>

        {isLoading && (
          <div className="flex flex-1 items-center justify-center">
            <PageSpinner className="size-[22px] text-subtle-foreground" />
          </div>
        )}

        {isEmpty && <EmptyState label={t('links.empty')} />}

        {!isLoading && !!links?.length && (
          <div className="grid gap-2 md:grid-cols-2 md:gap-4">
            {links.map((link) => (
              <ExternalLinkCard key={link.url} link={link} onOpen={openLink} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};
